import { z } from 'zod';
import { ApiError, type Schemas } from '../../api/client';

export type Contact = Schemas['Contact'];
export type PartyBalance = Schemas['PartyBalance'];
export type OpeningBalance = Schemas['OpeningBalance'];
export type ContactKind = 'customer' | 'supplier';
export type Ledger = 'RECEIVABLE' | 'PAYABLE';

/** Wording per kind of contact, so one set of screens serves customers and suppliers. */
export const KIND = {
  customer: {
    title: 'Customers',
    singular: 'customer',
    base: '/customers',
    ledger: 'RECEIVABLE' as Ledger,
    perm: 'customers',
  },
  supplier: {
    title: 'Suppliers',
    singular: 'supplier',
    base: '/suppliers',
    ledger: 'PAYABLE' as Ledger,
    perm: 'suppliers',
  },
} as const;

export const contactKeys = {
  all: ['contacts'] as const,
  list: (params: object) => ['contacts', 'list', params] as const,
  detail: (id: string) => ['contacts', 'detail', id] as const,
  ledger: (id: string, params: object) => ['contacts', 'detail', id, 'ledger', params] as const,
  statement: (id: string, params: object) =>
    ['contacts', 'detail', id, 'statement', params] as const,
  openItems: (id: string, ledger: Ledger) =>
    ['contacts', 'detail', id, 'open-items', ledger] as const,
  openingBalances: (id: string) => ['contacts', 'detail', id, 'opening-balances'] as const,
  duplicates: (params: object) => ['contacts', 'duplicates', params] as const,
};

// --- form: create / edit ---------------------------------------------------------------

const AMOUNT = /^\d{1,15}(\.\d{1,4})?$/;
const optionalAmount = z
  .string()
  .trim()
  .refine((v) => v === '' || AMOUNT.test(v), 'Enter an amount like 25000 or 25000.50');

export const contactFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(200, 'At most 200 characters'),
    phone: z
      .string()
      .trim()
      .max(30, 'At most 30 characters')
      .refine((v) => v === '' || /^[+\d][\d\s()-]{3,}$/.test(v), 'Enter a valid phone number'),
    email: z
      .string()
      .trim()
      .max(254)
      .refine((v) => v === '' || z.email().safeParse(v).success, 'Enter a valid email address'),
    address: z.string().trim().max(500, 'At most 500 characters'),
    taxNumber: z.string().trim().max(50, 'At most 50 characters'),
    notes: z.string().trim().max(2000, 'At most 2000 characters'),
    isCustomer: z.boolean(),
    isSupplier: z.boolean(),
    creditLimit: optionalAmount,
    paymentTermsDays: z
      .string()
      .trim()
      .refine(
        (v) => v === '' || (/^\d{1,4}$/.test(v) && Number(v) <= 3650),
        'Whole days, 0 to 3650',
      ),
  })
  .refine((v) => v.isCustomer || v.isSupplier, {
    message: 'Choose customer, supplier or both',
    path: ['isCustomer'],
  });

export type ContactForm = z.infer<typeof contactFormSchema>;

export function emptyContactForm(kind: ContactKind): ContactForm {
  return {
    name: '',
    phone: '',
    email: '',
    address: '',
    taxNumber: '',
    notes: '',
    isCustomer: kind === 'customer',
    isSupplier: kind === 'supplier',
    creditLimit: '',
    paymentTermsDays: '',
  };
}

export function contactToForm(c: Contact): ContactForm {
  return {
    name: c.name,
    phone: c.phone ?? '',
    email: c.email ?? '',
    address: c.address ?? '',
    taxNumber: c.taxNumber ?? '',
    notes: c.notes ?? '',
    isCustomer: c.isCustomer,
    isSupplier: c.isSupplier,
    // The API returns money at 4 places ("200000.0000"); show it as typed.
    creditLimit: c.creditLimit === null ? '' : trimDecimal(c.creditLimit),
    paymentTermsDays: c.paymentTermsDays === null ? '' : String(c.paymentTermsDays),
  };
}

const blank = (v: string) => (v.trim() === '' ? null : v.trim());

export function toContactRequest(f: ContactForm): Schemas['CreateContactRequest'] {
  return {
    name: f.name.trim(),
    phone: blank(f.phone),
    email: blank(f.email),
    address: blank(f.address),
    taxNumber: blank(f.taxNumber),
    notes: blank(f.notes),
    isCustomer: f.isCustomer,
    isSupplier: f.isSupplier,
    // A credit limit only means something for customers.
    creditLimit: f.isCustomer ? blank(f.creditLimit) : null,
    paymentTermsDays: f.paymentTermsDays.trim() === '' ? null : Number(f.paymentTermsDays),
  };
}

/** "200000.0000" → "200000", "12.5000" → "12.5". */
export function trimDecimal(value: string): string {
  if (!value.includes('.')) return value;
  return value.replace(/\.?0+$/, '');
}

// --- opening balances -------------------------------------------------------------------

export const openingBalanceSchema = (today: string) =>
  z.object({
    ledger: z.enum(['RECEIVABLE', 'PAYABLE']),
    direction: z.enum(['OWED_TO_SHOP', 'OWED_BY_SHOP']),
    amount: z
      .string()
      .trim()
      .refine((v) => AMOUNT.test(v), 'Enter an amount like 45000 or 45000.50')
      .refine((v) => !AMOUNT.test(v) || Number(v) > 0, 'Must be more than zero'),
    effectiveDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Pick a date')
      .refine((v) => v <= today, 'Cannot be in the future'),
    source: z
      .string()
      .trim()
      .min(3, 'Say where this balance comes from (at least 3 characters)')
      .max(500, 'At most 500 characters'),
  });

export type OpeningBalanceForm = z.infer<ReturnType<typeof openingBalanceSchema>>;

/** Plain-language choices for the direction of an opening balance on each ledger. */
export function directionOptions(ledger: Ledger, party: string) {
  const owesShop = {
    value: 'OWED_TO_SHOP' as const,
    label: `${party} owes the shop`,
    hint: ledger === 'RECEIVABLE' ? 'Unpaid sales' : 'Advance paid to them',
  };
  const shopOwes = {
    value: 'OWED_BY_SHOP' as const,
    label: `The shop owes ${party}`,
    hint: ledger === 'RECEIVABLE' ? 'Advance or credit they have with you' : 'Unpaid bills',
  };
  return ledger === 'RECEIVABLE' ? [owesShop, shopOwes] : [shopOwes, owesShop];
}

export function defaultDirection(ledger: Ledger): 'OWED_TO_SHOP' | 'OWED_BY_SHOP' {
  return ledger === 'RECEIVABLE' ? 'OWED_TO_SHOP' : 'OWED_BY_SHOP';
}

/** What the confirmation says the posting will do (UI_UX_GUIDELINES: state the financial effect). */
export function openingBalanceEffect(
  f: Pick<OpeningBalanceForm, 'ledger' | 'direction'>,
  name: string,
  amount: string,
): string {
  const ledger = f.ledger === 'RECEIVABLE' ? 'receivable' : 'payable';
  const who = f.direction === 'OWED_TO_SHOP' ? `${name} owes the shop` : `the shop owes ${name}`;
  return `This posts ${amount} to the ${ledger} ledger against Opening Balance Equity, recording that ${who}.`;
}

// --- balances ---------------------------------------------------------------------------

export type Tone = 'success' | 'warning' | 'error' | 'info' | 'default';

/** Colour meaning of a balance from the shop's point of view: money owed to the shop is "warning". */
export function balanceTone(b: Pick<PartyBalance, 'direction'>): Tone {
  if (b.direction === 'SETTLED') return 'success';
  return b.direction === 'OWED_TO_SHOP' ? 'warning' : 'info';
}

export interface CreditUsage {
  limit: number;
  used: number;
  available: number;
  /** 0–100 for a progress bar. */
  percent: number;
  over: boolean;
}

/** Credit limit use: only money the customer owes counts against the limit. */
export function creditUsage(c: Pick<Contact, 'creditLimit' | 'balances'>): CreditUsage | null {
  if (c.creditLimit === null) return null;
  const limit = Number(c.creditLimit);
  if (!Number.isFinite(limit)) return null;
  const used = Math.max(0, Number(c.balances.receivable.signed) || 0);
  const available = limit - used;
  const percent = limit > 0 ? Math.min(100, (used / limit) * 100) : used > 0 ? 100 : 0;
  return { limit, used, available, percent, over: used > limit };
}

// --- dates ------------------------------------------------------------------------------

/** Today's business date (YYYY-MM-DD) in the shop's time zone. */
export function todayIn(timeZone: string, now = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

export type RangePreset = 'thisMonth' | 'lastMonth' | 'last90' | 'thisYear' | 'all';

export const RANGE_PRESETS: { value: RangePreset; label: string }[] = [
  { value: 'thisMonth', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'thisYear', label: 'This year' },
  { value: 'all', label: 'All time' },
];

const iso = (y: number, m: number, d: number) =>
  new Date(Date.UTC(y, m, d)).toISOString().slice(0, 10);

/** A date range for a preset, relative to `today` (YYYY-MM-DD). `from` empty = from the beginning. */
export function presetRange(preset: RangePreset, today: string): { from: string; to: string } {
  const [y = 1970, m = 1, d = 1] = today.split('-').map(Number);
  switch (preset) {
    case 'thisMonth':
      return { from: iso(y, m - 1, 1), to: today };
    case 'lastMonth':
      return { from: iso(y, m - 2, 1), to: iso(y, m - 1, 0) };
    case 'last90':
      return { from: iso(y, m - 1, d - 89), to: today };
    case 'thisYear':
      return { from: iso(y, 0, 1), to: today };
    case 'all':
      return { from: '', to: today };
  }
}

/** Where a ledger document opens in the app (screens built by other milestones). */
export function documentHref(type: string, id: string): string | null {
  switch (type) {
    case 'SALES_INVOICE':
      return `/sales/invoices/${id}`;
    case 'SUPPLIER_INVOICE':
      return `/purchases/bills/${id}`;
    case 'PAYMENT':
      return `/payments/${id}`;
    default:
      return null;
  }
}

// --- API helpers ----------------------------------------------------------------------

/**
 * Idempotency-Key header for POSTs whose schema requires it. The client would add one anyway; this
 * satisfies the generated types and keeps one key per call.
 */
export function idempotency(): { 'idempotency-key': string } {
  return { 'idempotency-key': crypto.randomUUID() };
}

// --- API errors -------------------------------------------------------------------------

/**
 * Maps API validation `details` (path "body.name") to form fields. Returns true when at least one
 * detail landed on a field, so the caller can skip the generic toast.
 */
export function applyApiErrors<F extends string>(
  error: unknown,
  fields: readonly F[],
  setError: (field: F, e: { type: string; message: string }) => void,
): boolean {
  if (!(error instanceof ApiError)) return false;
  let mapped = false;
  for (const d of error.details) {
    const field = d.path.replace(/^body\./, '').split('.')[0] as F;
    if (fields.includes(field)) {
      setError(field, { type: 'server', message: d.message });
      mapped = true;
    }
  }
  return mapped;
}
